// itemFlat/ItemFlat.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MdFavorite,
  MdFavoriteBorder,
  MdCategory,
  MdStar,
} from "react-icons/md";
import { SaleItemInterface } from "../../../../../common/types/types";
import { useItem } from "../useItem";
import { useTheme } from "../../../../../common/hooks/useTheme";
import { useCartActions } from "../../../../../common/hooks/useCartActions";
import styles from "./ItemFlat.module.scss";
import { getImageUri } from "../../../../../common/utils/functions/getImageUri";
import { shortenText } from "../../../../../common/utils/functions/shortenText";
import { ROUTES } from "../../../../../common/utils/constants";
import { ItemCardButton } from "../../../../../common/components/itemCartButton/ItemCardButton";
import { formatPriceToLocale } from "../../../../../common/utils/functions/formatPriceToLocale";
import { useDisplayPrice } from "../../../../../common/hooks/useDisplayPrice";
import { RootState } from "../../../../../store/store";

type Props = {
  item: SaleItemInterface;
  getCategoryName: (id: number) => string | undefined;
  isAuth?: boolean;
  usingStock?: boolean;
};

export const ItemFlat = ({
  item,
  getCategoryName,
  isAuth,
  usingStock,
}: Props) => {
  const { theme } = useTheme();
  const getPrice = useDisplayPrice();

  const {
    isFavorite,
    renderData,
    handleFavoritePress,
    currentUser,
    isOutOfStock,
    isIncreaseDisabled,
  } = useItem(item);

  const {
    quantity,
    isComplexCategory,
    showComplexButton,
    showCartButton,
    handleAddToCart,
    handleAddToComplex,
    handleIncrease,
    handleDecrease,
    handleRemove,
  } = useCartActions(item);

  const isInComplexCart = useSelector((state: RootState) =>
    state.complexOrderCart.items.some((i) => i.itemId === item.id),
  );

  const displayPrice = getPrice(item.prices);
  const hasPrice = Boolean(displayPrice);

  const firstImage =
    item.files?.find((p) => !p.includes("video_") && !p.includes("poster_")) ||
    item.files?.find((p) => p.includes("poster_")) ||
    null;

  if (!item) return null;

  return (
    <Link to={`${ROUTES.ITEM}/${item.id}`} className={styles.container}>
      <div className={styles.imageContainer}>
        {firstImage && (
          <img
            className={styles.image}
            src={`${getImageUri()}${firstImage}`}
            alt={item.name}
          />
        )}

        {currentUser && (
          <button
            className={styles.favoriteButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFavoritePress();
            }}
            aria-label={
              isFavorite ? "Удалить из избранного" : "Добавить в избранное"
            }
          >
            {isFavorite ? (
              <MdFavorite size={16} color="#fff" />
            ) : (
              <MdFavoriteBorder size={16} color="#fff" />
            )}
          </button>
        )}
      </div>

      <div className={styles.body}>
        <span className={styles.nameText} title={item.name}>
          {item.name}
        </span>

        <span className={styles.priceText}>
          {hasPrice ? `${formatPriceToLocale(displayPrice)} ₽` : "Нет цены"}
        </span>

        <div className={styles.metaRow}>
          <MdCategory size={12} color={theme.textSecondary} />
          <span className={styles.category}>
            {shortenText(getCategoryName(Number(item.categoryId)), 15)}
          </span>

          {renderData.showRating && (
            <div className={styles.ratingContainer}>
              <MdStar size={12} color="#FFD700" />
              <span className={styles.ratingText}>
                {renderData.averageRating!.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <ItemCardButton
            quantity={quantity}
            showCartButton={showCartButton}
            showComplexButton={showComplexButton}
            isComplexCategory={isComplexCategory}
            isOutOfStock={isOutOfStock}
            isIncreaseDisabled={isIncreaseDisabled}
            hasPrice={hasPrice}
            isInComplexCart={isInComplexCart}
            onAddToCart={handleAddToCart}
            onAddToComplex={handleAddToComplex}
            onIncrease={handleIncrease}
            onDecrease={handleDecrease}
            onRemove={handleRemove}
            appearance ={'compact'}
          />
        </div>
      </div>
    </Link>
  );
};
